/**
 * A questionnaire step: the project's own form (`GET /collection-flow/questionnaire`),
 * rendered as one scrolling page that grows as answers come in — a branch
 * appears once the question it depends on is answered.
 *
 * Sent as `QUESTIONNAIRE_SUBMITTED` `{version_id, answers}`, keyed by node id,
 * visible questions only. The server re-walks the branches and refuses an
 * answer off the path, so `walk` here must match Rust (`walk.test.ts`).
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ACCEPTED_TYPES, problemWith, uploadDocumentFile } from '../businessDocument';
import { apiUrl, authHeaders, CollectionFlowError, postCollectionFlowEvent } from '../client';
import { CountrySelect } from '../CountrySelect';
import { DateInput } from '../DateInput';
import { type DateRange, todayYmd } from '../dateField';
import { PhoneInput } from '../PhoneInput';
import type { CollectionFlowSnapshot } from '../types';
import { type Draft, normalize, type Problem, problemOf, toPayload, type UploadedFile } from './answers';
import type { Answer, ApplicantQuestionnaire, L10n, Question } from './types';
import { walk } from './walk';

export const QUESTIONNAIRE_EVENT = 'QUESTIONNAIRE_SUBMITTED';

export interface QuestionnaireView {
  kind: 'questionnaire';
  questionnaire_id: string;
}

export function questionnaireOf(step: unknown): QuestionnaireView | null {
  if (typeof step !== 'object' || step === null) return null;
  const s = step as Partial<QuestionnaireView>;
  return s.kind === 'questionnaire' && typeof s.questionnaire_id === 'string'
    ? (s as QuestionnaireView)
    : null;
}

export async function getQuestionnaire(token: string): Promise<ApplicantQuestionnaire> {
  const response = await fetch(apiUrl('/api/v1/collection-flow/questionnaire'), {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { code?: string } | null;
    throw new CollectionFlowError(response.status, `questionnaire ${response.status}`, body?.code);
  }
  return (await response.json()) as ApplicantQuestionnaire;
}

/** Any date a questionnaire may ask about: well into the past and the future. */
function anyDateRange(): DateRange {
  const today = todayYmd();
  return {
    min: { year: today.year - 150, month: 1, day: 1 },
    max: { year: today.year + 50, month: 12, day: 31 },
    opensAt: today,
  };
}

function Field({
  question,
  id,
  draft,
  onChange,
  problem,
  lang,
  fallback,
  token,
}: {
  question: Question;
  id: string;
  draft: Draft;
  onChange: (d: Draft) => void;
  problem: Problem | null;
  lang: string;
  fallback: string;
  token: string;
}) {
  const { t } = useTranslation();
  const tx = (m: L10n | undefined) => (m ? m[lang] || m[fallback] || '' : '');
  const title = tx(question.title);
  const help = tx(question.description);
  const placeholder = tx(question.placeholder);
  const fieldId = `q-${id}`;
  const errorId = `${fieldId}-error`;
  const custom = tx(question.validation?.error_message);
  const message = problem
    ? problem === 'format' || problem === 'length' || problem === 'range'
      ? custom || t(`questionnaire.problem.${problem}`)
      : t(`questionnaire.problem.${problem}`)
    : null;
  const describedBy = message ? errorId : undefined;
  const et = question.element_type;
  const uploadInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProblem, setUploadProblem] = useState<string | null>(null);

  const label = (
    <span className="details-label" id={`${fieldId}-label`}>
      {title}
      {question.required ? null : (
        <span className="questionnaire-optional"> · {t('questionnaire.optional')}</span>
      )}
    </span>
  );
  const helpText = help ? <span className="step-text questionnaire-help">{help}</span> : null;
  const error = message ? (
    <span className="questionnaire-error" id={errorId} role="alert">
      {message}
    </span>
  ) : null;

  const textValue = typeof draft === 'string' ? draft : '';

  if (et === 'consent') {
    return (
      <div className="details-field">
        <label className="company-result questionnaire-consent">
          <input
            aria-describedby={describedBy}
            checked={draft === true}
            id={fieldId}
            onChange={(e) => onChange(e.target.checked)}
            type="checkbox"
          />
          <span className="company-result__body">
            <span>{title}</span>
            {help ? <span className="step-text">{help}</span> : null}
          </span>
        </label>
        {error}
      </div>
    );
  }

  if (et === 'dropdown' || et === 'single_choice' || et === 'multiple_choice') {
    const multi = et === 'multiple_choice';
    const chosen: { value: string | string[]; text: string } =
      typeof draft === 'object' && draft !== null && 'value' in draft
        ? draft
        : { value: multi ? [] : '', text: '' };
    const selected = (v: string) =>
      Array.isArray(chosen.value) ? chosen.value.includes(v) : chosen.value === v;
    const asksText = !multi && question.choices?.find((c) => c.value === chosen.value)?.requires_text_input;
    const choiceText = (
      <input
        aria-label={t('questionnaire.otherDetails')}
        className="details-input"
        onChange={(e) => onChange({ value: chosen.value as string, text: e.target.value })}
        placeholder={t('questionnaire.otherDetails')}
        value={chosen.text}
      />
    );
    if (et === 'dropdown') {
      return (
        <div className="details-field">
          <label className="details-field" htmlFor={fieldId}>
            {label}
            {helpText}
          </label>
          <select
            aria-describedby={describedBy}
            className="details-input"
            id={fieldId}
            onChange={(e) => onChange({ value: e.target.value, text: '' })}
            value={typeof chosen.value === 'string' ? chosen.value : ''}
          >
            <option value="">{placeholder || t('questionnaire.choose')}</option>
            {question.choices?.map((c) => (
              <option key={c.value} value={c.value}>
                {tx(c.label) || c.value}
              </option>
            ))}
          </select>
          {asksText ? choiceText : null}
          {error}
        </div>
      );
    }
    return (
      <fieldset aria-describedby={describedBy} className="details-field questionnaire-fieldset">
        <legend className="details-label">
          {title}
          {question.required ? null : (
            <span className="questionnaire-optional"> · {t('questionnaire.optional')}</span>
          )}
        </legend>
        {helpText}
        <ul className="company-results">
          {question.choices?.map((c) => (
            <li key={c.value}>
              <label className="company-result">
                <input
                  checked={selected(c.value)}
                  name={fieldId}
                  onChange={(e) => {
                    if (!multi) {
                      onChange({ value: c.value, text: '' });
                      return;
                    }
                    const list = Array.isArray(chosen.value) ? chosen.value : [];
                    onChange({
                      value: e.target.checked ? [...list, c.value] : list.filter((x) => x !== c.value),
                      text: '',
                    });
                  }}
                  type={multi ? 'checkbox' : 'radio'}
                  value={c.value}
                />
                <span className="company-result__body">{tx(c.label) || c.value}</span>
              </label>
            </li>
          ))}
        </ul>
        {asksText ? choiceText : null}
        {error}
      </fieldset>
    );
  }

  if (et === 'file_upload' || et === 'image') {
    const list: UploadedFile[] =
      typeof draft === 'object' && draft !== null && 'files' in draft ? draft.files : [];
    const max = question.max_files ?? 1;
    const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (et === 'image' && !file.type.startsWith('image/')) {
        setUploadProblem(t('questionnaire.problem.imageOnly'));
        return;
      }
      const found = problemWith(file);
      if (found) {
        setUploadProblem(t(`flow.businessDocument.${found}`));
        return;
      }
      setUploading(true);
      setUploadProblem(null);
      try {
        // Never an `id-` prefix: the operator review lists `id-%` files as the
        // identity document's images.
        const fileId = await uploadDocumentFile(token, file, `questionnaire-${question.value}`);
        onChange({ files: [...list, { id: fileId, name: file.name }] });
      } catch {
        setUploadProblem(t('flow.businessDocument.failed'));
      } finally {
        setUploading(false);
      }
    };
    return (
      <div className="details-field">
        {label}
        {helpText}
        {list.length ? (
          <ul className="questionnaire-files">
            {list.map((f) => (
              <li key={f.id}>
                <span>{f.name}</span>
                <button
                  className="btn btn--ghost"
                  onClick={() => onChange({ files: list.filter((x) => x.id !== f.id) })}
                  type="button"
                >
                  {t('questionnaire.removeFile')}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <input
          accept={et === 'image' ? 'image/*' : ACCEPTED_TYPES}
          aria-labelledby={`${fieldId}-label`}
          data-testid={`questionnaire-file-${id}`}
          hidden
          onChange={(e) => void onPick(e)}
          ref={uploadInput}
          type="file"
        />
        {list.length < max ? (
          <button
            aria-describedby={describedBy}
            className="btn btn--ghost"
            disabled={uploading}
            onClick={() => uploadInput.current?.click()}
            type="button"
          >
            {uploading ? t('flow.businessDocument.uploading') : t('questionnaire.addFile', { count: max - list.length })}
          </button>
        ) : null}
        {uploadProblem ? (
          <span className="questionnaire-error" role="alert">
            {uploadProblem}
          </span>
        ) : null}
        {error}
      </div>
    );
  }

  if (et === 'country' || et === 'phone' || et === 'date_picker') {
    return (
      <div className="details-field">
        <span aria-hidden="true" className="details-label">
          {title}
          {question.required ? null : (
            <span className="questionnaire-optional"> · {t('questionnaire.optional')}</span>
          )}
        </span>
        {helpText}
        {et === 'country' ? (
          <CountrySelect
            label={title}
            name={fieldId}
            onChange={onChange}
            placeholder={placeholder || t('questionnaire.choose')}
            value={textValue}
          />
        ) : et === 'phone' ? (
          <PhoneInput id={fieldId} label={title} name={fieldId} onChange={onChange} value={textValue} />
        ) : (
          <DateInput
            label={title}
            name={fieldId}
            onChange={onChange}
            placeholder={placeholder || t('questionnaire.chooseDate')}
            range={anyDateRange()}
            value={textValue}
          />
        )}
        {error}
      </div>
    );
  }

  const v = question.validation ?? {};
  const common = {
    'aria-describedby': describedBy,
    'aria-invalid': message ? true : undefined,
    className: 'details-input',
    id: fieldId,
    maxLength: v.max_length ?? undefined,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value),
    placeholder: placeholder || undefined,
    value: textValue,
  };
  return (
    <label className="details-field" htmlFor={fieldId}>
      {label}
      {helpText}
      {et === 'long_text' ? (
        <textarea rows={4} {...common} className="details-input questionnaire-textarea" />
      ) : (
        <input
          {...common}
          inputMode={et === 'number' ? 'decimal' : undefined}
          type={et === 'email' ? 'email' : et === 'time' ? 'time' : 'text'}
        />
      )}
      {error}
    </label>
  );
}

export function QuestionnaireStep({
  token,
  onAdvance,
}: {
  token: string;
  view: QuestionnaireView;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
}) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<ApplicantQuestionnaire | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [showProblems, setShowProblems] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getQuestionnaire(token)
      .then((f) => live && setForm(f))
      .catch(() => live && setLoadFailed(true));
    return () => {
      live = false;
    };
  }, [token]);

  const lang = form
    ? form.languages.includes(i18n.language)
      ? i18n.language
      : form.default_language
    : i18n.language;

  const answers = useMemo(() => {
    const out: Record<string, Answer | null> = {};
    if (!form) return out;
    for (const [id, draft] of Object.entries(drafts)) {
      const n = form.graph.nodes[id];
      if (n?.kind === 'question') out[id] = normalize(n, draft);
    }
    return out;
  }, [form, drafts]);

  if (loadFailed) {
    return (
      <div className="details-form">
        <h1 className="step-title">{t('questionnaire.title')}</h1>
        <p className="alert" role="alert">
          {t('questionnaire.unavailable')}
        </p>
      </div>
    );
  }
  if (!form) {
    return (
      <div className="details-form" aria-busy="true">
        <h1 className="step-title">{t('questionnaire.title')}</h1>
        <p className="step-text">{t('questionnaire.loading')}</p>
      </div>
    );
  }

  const graph = form.graph;
  const heading = form.title || t('questionnaire.title');
  const shown = walk(graph, answers, true);
  const fullPath = walk(graph, answers);
  const waiting = shown.length < fullPath.length;
  const tx = (m: L10n | undefined) => (m ? m[lang] || m[form.default_language] || '' : '');
  const questions = shown.filter((id) => graph.nodes[id]?.kind === 'question');
  const problems: Record<string, Problem | null> = {};
  for (const id of questions) {
    const n = graph.nodes[id];
    if (n?.kind === 'question') problems[id] = problemOf(n, drafts[id]);
  }
  const firstProblem = questions.find((id) => problems[id]);

  const submit = async () => {
    setShowProblems(true);
    if (firstProblem || waiting) {
      document.getElementById(`q-${firstProblem}`)?.focus();
      return;
    }
    const payload: Record<string, unknown> = {};
    for (const id of walk(graph, answers)) {
      const n = graph.nodes[id];
      if (n?.kind !== 'question') continue;
      const p = toPayload(n, drafts[id]);
      if (p !== undefined) payload[id] = p;
    }
    setBusy(true);
    setProblem(null);
    setServerErrors({});
    try {
      onAdvance(
        await postCollectionFlowEvent(token, QUESTIONNAIRE_EVENT, {
          version_id: form.version_id,
          answers: payload,
        }),
      );
    } catch (err) {
      const errors =
        err instanceof CollectionFlowError && err.code === 'invalid_answers'
          ? ((err.details?.errors as Record<string, string> | undefined) ?? {})
          : {};
      setServerErrors(errors);
      setProblem(
        Object.keys(errors).length ? t('questionnaire.checkAnswers') : t('questionnaire.failed'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="details-form questionnaire"
      lang={lang}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h1 className="step-title">{heading}</h1>
      {form.description ? <p className="step-text">{form.description}</p> : null}
      {shown.map((id) => {
        const node = graph.nodes[id];
        if (!node || node.kind === 'branching') return null;
        if (node.kind !== 'question') {
          const title = tx(node.title);
          const body = tx(node.description);
          // A section named like the page says nothing new: keep one heading.
          const repeatsHeading = title.trim().toLowerCase() === heading.trim().toLowerCase();
          return node.kind === 'section' ? (
            <div className="questionnaire-section" key={id}>
              {repeatsHeading ? null : <h2 className="questionnaire-section__title">{title}</h2>}
              {body ? <p className="step-text">{body}</p> : null}
            </div>
          ) : (
            <div className="questionnaire-text" key={id}>
              {title ? <h3 className="details-label">{title}</h3> : null}
              {body ? <p className="step-text">{body}</p> : null}
            </div>
          );
        }
        const local = showProblems ? problems[id] : null;
        const fromServer = serverErrors[id] ? (serverErrors[id] === 'required' ? 'required' : 'format') : null;
        return (
          <Field
            draft={drafts[id]}
            fallback={form.default_language}
            id={id}
            key={id}
            lang={lang}
            onChange={(d) => setDrafts((prev) => ({ ...prev, [id]: d }))}
            problem={local ?? (fromServer as Problem | null)}
            question={node}
            token={token}
          />
        );
      })}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="actions">
        <button className="btn" disabled={busy} type="submit">
          {busy ? t('questionnaire.sending') : t('questionnaire.submit')}
        </button>
      </div>
    </form>
  );
}
